import React from "react";
import { IconButton, Tooltip } from "@mui/material";
import GitHubIcon from "@mui/icons-material/GitHub";
import CodeIcon from "@mui/icons-material/Code";
import { theme } from "../../../../Themes/Themes";
import useStore from "../../../../Store/Store";

interface ExternalLinksButtonsProps {
  startLine?: number;
  endLine?: number;
  filePath: string;
  topOffset?: string;
}

const ExternalLinksButtons: React.FC<ExternalLinksButtonsProps> = ({
  startLine,
  endLine,
  filePath,
  topOffset,
}) => {
  const { currentRepo, currentRepoMetadata, currentBranch, setDialog } =
    useStore((state) => ({
      currentRepo: state.currentRepo,
      currentRepoMetadata: state.currentRepoMetadata,
      currentBranch: state.currentBranch,
      setDialog: state.setDialog,
    }));

  const buildGitHubUrl = () => {
    if (
      !currentRepo ||
      !currentRepoMetadata?.owner ||
      !currentBranch ||
      !filePath
    ) {
      return null;
    }

    const owner = currentRepoMetadata.owner;
    let url = `https://github.com/${owner}/${currentRepo}/blob/${currentBranch}/${filePath}`;

    if (startLine) {
      if (endLine && endLine !== startLine) {
        url += `#L${startLine}-L${endLine}`;
      } else {
        url += `#L${startLine}`;
      }
    }

    return url;
  };

  const buildVSCodeUrl = (repoPath: string) => {
    if (!repoPath || !filePath) {
      return null;
    }

    const fullPath = `${repoPath}/${filePath}`;
    let url = `vscode://file${fullPath}`;

    if (startLine) {
      url += `:${startLine}:1`;
    }

    return url;
  };

  const handleGitHubClick = () => {
    const githubUrl = buildGitHubUrl();
    if (githubUrl) {
      window.open(githubUrl, "_blank");
    }
  };

  const handleVSCodeClick = () => {
    const repoKey = `vscodeRepoPath_${currentRepo}`;
    const workingKey = `vscodeWorking_${currentRepo}`;

    const storedPath = localStorage.getItem(repoKey);
    const isWorking = localStorage.getItem(workingKey) === "true";

    if (storedPath && isWorking) {
      // We have a working path, try to open directly
      const vscodeUrl = buildVSCodeUrl(storedPath);
      if (vscodeUrl) {
        window.open(vscodeUrl, "_self");
      }
    } else if (storedPath) {
      // We have a path but need to verify it works
      const vscodeUrl = buildVSCodeUrl(storedPath);
      if (vscodeUrl) {
        setDialog("DIALOG", {
          message: (
            <div>
              <div style={{ marginBottom: "16px" }}>
                Attempting to open in VSCode using stored path:
              </div>
              <div
                style={{
                  fontFamily: "monospace",
                  backgroundColor: "#f5f5f5",
                  padding: "8px",
                  borderRadius: "4px",
                  marginBottom: "16px",
                }}
              >
                {storedPath}
              </div>
              <div>
                Did VSCode open with the correct file
                {startLine ? ` at line ${startLine}` : ""}?
              </div>
            </div>
          ),
          button1Label: "Yes, it worked",
          button1Function: () => {
            localStorage.setItem(workingKey, "true");
          },
          button2Label: "No, let me fix it",
          button2Function: () => {
            // Small delay to ensure the current dialog closes first
            setTimeout(() => {
              setDialog("REPOSITORY_SETTINGS_DIALOG");
            }, 100);
          },
        });

        // Try to open VSCode
        window.open(vscodeUrl, "_self");
      }
    } else {
      // No stored path, prompt user to set it up
      setDialog("DIALOG", {
        message: (
          <div>
            <div style={{ marginBottom: "16px" }}>
              To open files in VSCode, you need to configure your local
              repository path.
            </div>
            <div>
              Would you like to set it up now, or open in GitHub instead?
            </div>
          </div>
        ),
        button1Label: "Set up VSCode",
        button1Function: () => {
          // Small delay to ensure the current dialog closes first
          setTimeout(() => {
            setDialog("REPOSITORY_SETTINGS_DIALOG");
          }, 100);
        },
        button2Label: "Open in GitHub",
        button2Function: () => {
          handleGitHubClick();
        },
      });
    }
  };

  const githubUrl = buildGitHubUrl();

  if (!githubUrl) {
    return null;
  }

  return (
    <div
      style={{
        position: "absolute",
        top: topOffset || "45px",
        right: "10px",
        display: "flex",
        gap: "8px",
        zIndex: 1000,
      }}
    >
      <Tooltip title="Open in Github" placement="bottom" arrow>
        <IconButton
          onClick={handleGitHubClick}
          sx={{
            width: "30px",
            height: "30px",
            border: `2px solid ${theme.custom.lighterPink}`,
            borderRadius: "50%",
            backgroundColor: "white",
            "&:hover": {
              backgroundColor: theme.custom.lighterPink,
              "& .MuiSvgIcon-root": {
                color: "white",
              },
            },
          }}
        >
          <GitHubIcon
            sx={{
              fontSize: "16px",
              color: theme.custom.lighterPink,
            }}
          />
        </IconButton>
      </Tooltip>

      <Tooltip
        title="Open in VSCode (configure repo path in File Menu → Repository Settings)"
        placement="bottom"
        arrow
      >
        <IconButton
          onClick={handleVSCodeClick}
          sx={{
            width: "30px",
            height: "30px",
            border: `2px solid ${theme.custom.lighterPink}`,
            borderRadius: "50%",
            backgroundColor: "white",
            "&:hover": {
              backgroundColor: theme.custom.lighterPink,
              "& .MuiSvgIcon-root": {
                color: "white",
              },
            },
          }}
        >
          <CodeIcon
            sx={{
              fontSize: "16px",
              color: theme.custom.lighterPink,
            }}
          />
        </IconButton>
      </Tooltip>
    </div>
  );
};

export default ExternalLinksButtons;
